import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageRenderer } from "../../components/PageRenderer";
import { CmsSeo } from "../../components/cms/CmsSeo";
import { ResourceLoading } from "../../components/utils/ResourceLoading";
import { ResourceError } from "../../components/utils/ResourceError";
import { PageHeading } from "../../components/utils/pageHeading";
import {
  documentExcerpt,
  formatPostDate,
  pagePath,
  postPath,
  unwrapCmsDocument,
} from "../../data/cmsPublic";

export function CmsDocumentPage({ kind, fetchDocument }) {
  const { slug } = useParams();
  const [document, setDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isPost = kind === "post";
  const missingLabel = isPost ? "Innlegget finnes ikke." : "Siden finnes ikke.";

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!slug) {
        setError("Slug mangler");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response = await fetchDocument(slug);
        if (cancelled) return;
        const next = unwrapCmsDocument(response?.data);
        if (!next) {
          setError(isPost ? "Kunne ikke laste innlegg" : "Kunne ikke laste side");
        } else {
          setDocument(next);
        }
      } catch (err) {
        if (cancelled) return;
        if (
          err.response?.status === 404 ||
          err.response?.data?.error === "WebPage not found"
        ) {
          setError(missingLabel);
        } else {
          setError(
            err.response?.data?.error ||
              err.message ||
              (isPost ? "Kunne ikke laste innlegg" : "Kunne ikke laste side")
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [slug, fetchDocument, isPost, missingLabel]);

  if (isLoading) {
    return (
      <section className="sec" style={{ borderTop: "none" }}>
        <div className="wrap" style={{ padding: "60px 40px" }}>
          <ResourceLoading />
        </div>
      </section>
    );
  }

  if (error) {
    if (error === missingLabel) {
      return (
        <>
          <PageHeading
            eyebrow="404"
            title={missingLabel}
            description="Beklager, innholdet du leter etter eksisterer ikke eller er ikke tilgjengelig lenger."
          />
          <section className="sec" style={{ borderTop: "none" }}>
            <div className="wrap" style={{ textAlign: "center" }}>
              <Link to={isPost ? "/innlegg" : "/"} className="btn">
                {isPost ? "Alle innlegg" : "Gå til forsiden"}{" "}
                <span className="arr">→</span>
              </Link>
            </div>
          </section>
        </>
      );
    }

    return (
      <section className="sec" style={{ borderTop: "none" }}>
        <div className="wrap">
          <ResourceError message={error} />
        </div>
      </section>
    );
  }

  const blocks = document?.content?.blocks || [];
  const pageWidth = document?.page_width || "full";
  const showTitle = document?.show_title !== false;
  const path = isPost ? postPath(document) : pagePath(document);
  const description = documentExcerpt(document);
  const innerMaxWidth = {
    wide: 1400,
    standard: 1200,
    narrow: 800,
  }[pageWidth];

  return (
    <>
      <CmsSeo document={document} kind={kind} path={path} />

      {showTitle && document?.title ? (
        <PageHeading
          eyebrow={isPost ? "Innlegg" : "Airon AS"}
          title={document.title}
          description={description || undefined}
        />
      ) : null}

      <section className="sec" style={{ borderTop: "none" }}>
        <div className="wrap">
          {isPost ? (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                marginBottom: 24,
                fontFamily: "var(--mono)",
                fontSize: 12,
                color: "var(--muted)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              <span>{document.author_name || "Airon AS"}</span>
              <span>{formatPostDate(document.published_at || document.created_at)}</span>
            </div>
          ) : null}

          {blocks.length === 0 ? (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                background: "#fff",
                border: "1px solid var(--line)",
                borderRadius: 16,
                color: "var(--muted)",
              }}
            >
              Ingen innhold funnet på {isPost ? "dette innlegget" : "denne siden"}.
            </div>
          ) : (
            <div
              style={
                innerMaxWidth
                  ? { maxWidth: innerMaxWidth, margin: "0 auto" }
                  : undefined
              }
            >
              <PageRenderer blocks={blocks} />
            </div>
          )}

          {isPost ? (
            <div style={{ marginTop: 40 }}>
              <Link to="/innlegg" className="btn">
                Flere innlegg <span className="arr">→</span>
              </Link>
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}
